const SANDBOX_HOSTS = new Set([
  'localhost',
  '127.0.0.1',
  'dev.portal.yandle.io',
  'www.dev.portal.yandle.io',
])

export const API_DOCS_ENVIRONMENTS = {
  production: {
    id: 'production',
    label: 'Production',
    apiBaseUrl: 'https://api.yandle.io',
    contactApiBaseUrl: 'https://contact-api.yandle.io',
  },
  sandbox: {
    id: 'sandbox',
    label: 'Development / Sandbox',
    apiBaseUrl: 'https://dev.api.yandle.io',
    contactApiBaseUrl: 'https://dev.contact-api.yandle.io',
  },
}

export function isApiDocsSandboxHost(hostname = typeof window !== 'undefined' ? window.location.hostname : '') {
  const host = String(hostname || '').trim().toLowerCase()
  if (!host) return false
  if (SANDBOX_HOSTS.has(host)) return true
  if (host.endsWith('.localhost')) return true
  return false
}

export function getApiDocsEnvironment(hostname) {
  return isApiDocsSandboxHost(hostname)
    ? API_DOCS_ENVIRONMENTS.sandbox
    : API_DOCS_ENVIRONMENTS.production
}

export function getApiDocsPublicBaseUrl(hostname) {
  return getApiDocsEnvironment(hostname).apiBaseUrl
}

function stripTags(html) {
  return String(html || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function rewriteApiDocsEndpointText(text, hostname) {
  const value = String(text ?? '')
  if (!value) return value

  const env = getApiDocsEnvironment(hostname)
  if (env.id === 'sandbox') {
    return value
      .replaceAll('https://contact-api.yandle.io', env.contactApiBaseUrl)
      .replaceAll('https://api.yandle.io', env.apiBaseUrl)
      .replaceAll('# Production token endpoint', '# Development / Sandbox token endpoint')
      .replaceAll('Production token endpoint', 'Development / Sandbox token endpoint')
  }

  return value
    .replaceAll('https://dev.contact-api.yandle.io', env.contactApiBaseUrl)
    .replaceAll('https://dev.api.yandle.io', env.apiBaseUrl)
    .replaceAll('# Development / Sandbox token endpoint', '# Production token endpoint')
    .replaceAll('Development / Sandbox token endpoint', 'Production token endpoint')
}

function isEnvironmentTableRow(row) {
  const label = stripTags(row?.[0]?.html).toLowerCase()
  return (
    label.includes('production')
    || label.includes('development')
    || label.includes('sandbox')
  )
}

function matchesCurrentEnvironment(row, env) {
  const label = stripTags(row?.[0]?.html).toLowerCase()
  if (env.id === 'sandbox') {
    return label.includes('development') || label.includes('sandbox')
  }
  return label.includes('production') && !label.includes('development')
}

function rewriteCell(cell, hostname) {
  if (!cell || typeof cell !== 'object') return cell
  return {
    ...cell,
    html: rewriteApiDocsEndpointText(cell.html, hostname),
  }
}

function rewriteTableBlock(block, hostname) {
  const env = getApiDocsEnvironment(hostname)
  const rows = Array.isArray(block.rows) ? block.rows : []
  const isEnvTable = block.variant === 'domain' || rows.some(isEnvironmentTableRow)
  const nextRows = (isEnvTable ? rows.filter((row) => matchesCurrentEnvironment(row, env)) : rows)
    .map((row) => row.map((cell) => rewriteCell(cell, hostname)))

  return {
    ...block,
    header: Array.isArray(block.header)
      ? block.header.map((cell) => rewriteCell(cell, hostname))
      : block.header,
    rows: nextRows.length ? nextRows : rows.map((row) => row.map((cell) => rewriteCell(cell, hostname))),
  }
}

function rewriteBlock(block, hostname) {
  if (!block || typeof block !== 'object') return block

  if (block.kind === 'code') {
    return {
      ...block,
      code: rewriteApiDocsEndpointText(block.code, hostname),
    }
  }

  if (block.kind === 'table') {
    return rewriteTableBlock(block, hostname)
  }

  if (block.kind === 'paragraph' || block.kind === 'callout' || block.kind === 'scope-note') {
    return {
      ...block,
      html: rewriteApiDocsEndpointText(block.html, hostname),
    }
  }

  if (block.kind === 'list') {
    return {
      ...block,
      items: (block.items || []).map((item) => rewriteApiDocsEndpointText(item, hostname)),
    }
  }

  return block
}

export function applyApiDocsEnvironment(doc, hostname) {
  if (!doc || typeof doc !== 'object') return doc

  const blocks = (doc.blocks || []).map((block) => rewriteBlock(block, hostname))
  const sections = (doc.sections || []).map((section) => {
    const nextBlocks = (section.blocks || []).map((block) => rewriteBlock(block, hostname))
    return {
      ...section,
      blocks: nextBlocks,
      description: rewriteApiDocsEndpointText(section.description, hostname),
      codes: nextBlocks.filter((b) => b.kind === 'code'),
      tables: nextBlocks.filter((b) => b.kind === 'table'),
      callouts: nextBlocks.filter((b) => b.kind === 'callout'),
      lists: nextBlocks.filter((b) => b.kind === 'list'),
    }
  })

  return {
    ...doc,
    environment: getApiDocsEnvironment(hostname),
    blocks,
    sections,
  }
}
